<template>
  <div>
    <Transition :name="transitionName" mode="out-in">
      <div class="widget-step-selector" :key="currentOption?.key">
        <h2
          v-if="!showBack"
          class="title3-bold widget-step-selector_title mb-4 font-medium text-primary-500"
          :class="componentStyles?.title || ''"
        >
          {{ currentOption?.context?.title }}
        </h2>
        <div v-else>
          <WButton type="ghost" class="group !-ml-2 mb-4 hover:border-transparent hover:bg-white" @click="goBack">
            <WidgetIcon :icon="themeIcons.chevronLeft" class="widget-primary-icon size-6" />
            <h2 class="widget-step-selector_title group-hover:underline" :class="componentStyles?.title || ''">
              {{ currentOption?.context?.title || 'BACK' }}
            </h2>
          </WButton>
        </div>
        <WAccordion
          v-if="currentOption?.context?.tips"
          class="mb-4"
          :bullets="currentOption.context?.tips?.bullets"
          :title="currentOption.context?.tips?.title"
        />

        <ul v-if="currentOption" role="list" :class="twMerge(selectorVariants({ variant }))">
          <li v-for="card in currentOption.context.options" :key="card.value" class="flex">
            <WButton
              type="ghost"
              :class="[twMerge(itemVariants({ variant })), componentStyles?.button?.step || '']"
              @click="selectCard(currentOption.key, card)"
            >
              <div class="flex items-center">
                <div class="mr-4 flex h-12 w-12 items-center justify-center rounded-full">
                  <WidgetIcon :icon="card.icon" class="widget-primary-icon size-12" />
                </div>
                <div v-if="checkTheme('landrover')" class="-ml-1 mr-2 flex items-center justify-center rounded-full">
                  <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
                </div>
                <div>
                  <h2 class="widget-step-selector_title" :class="[{ uppercase: checkTheme('jlr', 'referral') }]">
                    {{ card.title }}
                  </h2>
                  <p
                    class="widget-step-selector_description"
                    :class="[{ 'font-widget-primary': checkTheme('jlr', 'referral') }, componentStyles?.body || '']"
                  >
                    {{ card.description }}
                  </p>
                </div>
              </div>
              <div
                v-if="checkTheme('volvo', 'tesla', 'aa', 'cinch', 'crashdaddy', 'jaguar', 'bmw', 'toyota', 'samsung')"
                class="flex h-12 items-center justify-center rounded-full"
              >
                <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
              </div>
            </WButton>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getClientTheme, getComponentStyle } from 'src/utils/config';
  import WButton from '../../base/Button.vue';
  import WAccordion from '../../base/Accordion.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import type { ContextSelector, SelectorOptions } from 'src/types/widget';
  import { sendAnalytics } from 'src/utils/analytics';

  const componentStyles = getComponentStyle();
  const selectorVariants = cva('flex flex-col gap-y-4 rounded-custom', {
    variants: {
      variant: {
        default: '',
        audi: '',
        samsung: '',
        volkswagen: '',
        bentley: '',
        vwg: '',
        mercedes: '',
        bmw: '',
        toyota: '',
        mopar: '',
        jeep: '',
        mini: '',
        jlr: '',
        common: '',
        referral: '',
        jaguar: '',
        landrover: 'gap-y-0 border-t border-x border-backgroundSoft-600',
        volvo: 'gap-y-0 border border-backgroundSoft-600 p-2',
        tesla: 'gap-y-0 border border-backgroundSoft-600 p-2',
        aa: 'gap-y-0 border border-backgroundSoft-600 p-2',
        suzuki: '',
        cinch: 'gap-y-0 border border-backgroundSoft-600 p-2',
        crashdaddy: 'gap-y-0 border border-backgroundSoft-600 p-2',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });

  const itemVariants = cva('items-start w-full p-4 border border-backgroundSoft-600 !rounded-custom', {
    variants: {
      variant: {
        default: '',
        audi: '',
        samsung: '',
        volkswagen: '',
        bentley: '',
        vwg: '',
        mercedes: '',
        bmw: 'justify-between',
        toyota: 'justify-between',
        mopar: 'justify-between',
        jeep: 'justify-between',
        mini: 'justify-between',
        jaguar: 'justify-between',
        jlr: 'justify-between',
        common: 'justify-between',
        referral: 'justify-between',
        landrover: '',
        volvo: 'py-3 px-4 justify-between border-0',
        tesla: 'py-3 px-4 justify-between border-0',
        aa: 'py-3 px-4 justify-between border-0',
        suzuki: 'py-3 px-4 justify-between border border-primary bg-white hover:bg-backgroundSoft-400',
        cinch: 'py-3 px-4 justify-between border-0',
        crashdaddy: 'py-3 px-4 justify-between border-0',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });

  type SelectorVariants = VariantProps<typeof selectorVariants>;

  const props = defineProps<{
    value?: Record<string, string>;
    context: Record<string, ContextSelector>;
    isBackButtonVisible: boolean;
    variant?: SelectorVariants['variant'];
  }>();
  const emit = defineEmits<{
    (event: 'onFinish', result: Record<string, string>): void;
    (event: 'onSelect', result: SelectorOptions): void;
    (event: 'onDeSelect', key: string): void;
    (event: 'back'): void;
  }>();

  const options = computed(() => Object.entries(props.context).map(([key, context]) => ({ key, context })));
  const currentIndex = ref(0);
  const currentOption = computed(() => options.value?.[currentIndex.value]);
  const contextValue = ref<Record<string, string>>({});
  const initialValues = props.value ? Object.keys(props.value)?.length : 0;
  const isMultipleAvailable = computed(() => options.value?.length - initialValues > 1);
  const showBack = computed(() => (props.isBackButtonVisible || currentIndex.value > 0) && isMultipleAvailable.value);
  const transitionName = ref<'slide-left' | 'slide-right'>('slide-right');
  if (initialValues && props.value) {
    currentIndex.value = initialValues > options.value?.length ? options.value?.length - 1 : initialValues - 1;
    contextValue.value = props.value;
    const currItem = options.value?.[currentIndex.value];
    if (currItem && currItem.key === currentOption.value.key) {
      const currCard = currItem.context.options.find((card) => props.value && card.value === props.value[currItem.key]);
      if (currCard) {
        contextValue.value[currItem.key] = currCard.value;
        currentIndex.value++;
      }
    }
  }
  watch(currentOption, (current) => {
    if (current.context.options?.length === 1) {
      selectCard(current.key, current.context.options[0]);
    }
  });

  function selectCard(key: string, card: SelectorOptions) {
    transitionName.value = 'slide-left';
    nextTick(() => {
      contextValue.value[key] = card.value;
      emit('onSelect', card);
      if (currentIndex.value === options.value.length - 1) {
        emit('onFinish', contextValue.value);
        sendAnalytics('event', 'step_change', {
          event_category: 'STEP',
          event_label: 'CONTEXT_SELECTED',
          value: contextValue.value,
          send_to: env.GA_ID,
        });
      } else {
        currentIndex.value++;
      }
    });
  }

  function goBack() {
    transitionName.value = 'slide-right';
    if (currentIndex.value === 0) {
      emit('back');
    } else {
      emit('onDeSelect', options.value[currentIndex.value - 1].key);
      currentIndex.value--;
    }
  }
</script>
